import { say } from "../words.ts";
import type { KeyTable } from "./controls/page.ts";
import type { HudFeed } from "./hud/store.ts";
import type { KeyHints } from "./touch/key-hints.ts";

/**
 * The key the door's line names: the table whose Interact key it reads, and the page's answer to whether a key may be
 * named (`touch/key-hints.ts`).
 */
export interface DoorKeys {
  readonly keys: Pick<KeyTable, "label" | "subscribe">;
  readonly hints: Pick<KeyHints, "getSnapshot" | "subscribe">;
}

/**
 * The prompt of a door entered with E (ADR-122 decision 2.8, PROTOCOL.md 6.93.0): while the character stands on such a door
 * the server says so with `doorPrompt`, and the HUD draws "Enter <name>" in a place of its own over the message line (`HudFeed.setPrompt`), so no reply and no
 * `doorRefused` line replaces or blanks it, until the character steps off or the door takes it through. The page keys on `shown`, never on the name, which is empty when the map has none; then the
 * line reads the unnamed words. E is answered by the interaction (`interact.ts`), which asks `shown`. While a keyboard
 * is in use the line names the key ("Press E to enter Name", ADR-061's amendment of 2026-10-31, decision 12); without
 * one it is the action alone ("Enter Name"). The line is also a button, which the HUD wires to the interaction.
 */
export class DoorPrompt {
  private text: string | null = null;
  private name = "";
  private shownNow = false;
  private readonly stops: (() => void)[] = [];

  /**
   * @param hud The HUD's store, which holds the message line.
   * @param named The key table and key hints that name the key; without them the line never does.
   */
  public constructor(
    private readonly hud: Pick<HudFeed, "setPrompt">,
    private readonly named?: DoorKeys,
  ) {
    if (named !== undefined) {
      this.stops.push(named.keys.subscribe(() => this.render()), named.hints.subscribe(() => this.render()));
    }
  }

  /**
   * Whether the character stands on a door that takes E.
   *
   * @returns True from a shown `doorPrompt` until the next hidden one, or a reset.
   */
  public get shown(): boolean {
    return this.shownNow;
  }

  /**
   * Takes a `doorPrompt`: shows the line, or clears the one it showed.
   *
   * @param shown Whether the character stands on the door.
   * @param name The target map's name, possibly empty.
   */
  public receive(shown: boolean, name: string): void {
    this.shownNow = shown;
    this.name = name;
    this.render();
  }

  /**
   * Stops following the key table and the key hints.
   */
  public destroy(): void {
    for (const stop of this.stops.splice(0)) {
      stop();
    }
  }

  /**
   * Words the line for the door the character stands on, naming the key while one may be named, and puts it on the HUD.
   */
  private render(): void {
    this.clear();
    if (!this.shownNow) {
      return;
    }
    const key = this.named !== undefined && this.named.hints.getSnapshot() ? this.named.keys.label("interact") : null;
    const name = this.name;
    this.text = key === null ? (name === "" ? say("door.enterUnnamed") : say("door.enter", { name })) : name === "" ? say("door.pressUnnamed", { key }) : say("door.press", { name, key });
    this.hud.setPrompt(this.text);
  }

  /**
   * Forgets the prompt, as a session ends or a map is reached.
   */
  public reset(): void {
    this.shownNow = false;
    this.clear();
  }

  /**
   * Takes the prompt off the HUD.
   */
  private clear(): void {
    if (this.text !== null) {
      this.hud.setPrompt(null);
      this.text = null;
    }
  }
}
